import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { Panel } from "../components/Surface";
import { Icon } from "../app/Icon";
import { SpecGrid, Tags, VerifiedPill, GatedNotice, MetaFooter, shortId } from "../components/HeroDetail";
import { SaveButton } from "../components/ResultCard";
import { labelize } from "../lib/format";
import {
  getJob, applyToJob, jobApplications, setApplicationStatus, setJobStatus,
  payLabel, bonusLabel, experienceLabel,
  type JobDetail as Job, type JobApplication,
} from "../lib/jobs";

const REVIEW_ACTIONS = ["reviewing", "accepted", "rejected"];

export function JobDetail() {
  const { id } = useParams();
  const { account } = useAuth();
  const { active } = useProfiles();
  const [job, setJob] = useState<Job | null>(null);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isOwner = Boolean(job && active && job.poster_profile_id === active.id);
  const isDriver = active?.type === "driver";

  async function loadJob() {
    if (!id) return;
    setJob((await getJob(id)).job);
  }
  useEffect(() => {
    void loadJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (isOwner && id) jobApplications(id).then((r) => setApplications(r.applications));
  }, [isOwner, id]);

  async function onApply() {
    if (!job || !active) return;
    setBusy(true);
    setNote(null);
    try {
      await applyToJob(job.id, active.id);
      setNote("Application sent.");
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Could not apply.");
    } finally {
      setBusy(false);
    }
  }
  async function decide(applicationId: string, status: string) {
    await setApplicationStatus(applicationId, status);
    if (id) setApplications((await jobApplications(id)).applications);
  }
  async function toggleStatus() {
    if (!job) return;
    const next = job.status === "open" ? "closed" : "open";
    await setJobStatus(job.id, next);
    setJob({ ...job, status: next });
  }

  if (!job) return <p className="p-10 font-mono text-sm text-muted">Loading…</p>;

  const pay = payLabel(job.pay_min, job.pay_max, job.pay_type);
  const bonus = bonusLabel(job.sign_on_bonus);
  const showGate = isDriver && !isOwner && !account?.identity_verified;

  return (
    <div className="grid gap-6">
      <Link to="/jobs" className="flex items-center gap-1 text-sm font-bold text-accent-strong">
        <Icon name="jobs" className="h-4 w-4" /> Job board
      </Link>

      {/* Hero card */}
      <div className="grid gap-5 rounded-card border border-line bg-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-2">
            <h1 className="font-display text-3xl font-black tracking-tight">{job.title}</h1>
            <VerifiedPill label={labelize(job.poster_type)} />
            {bonus && <p className="font-mono text-sm font-bold text-accent-strong">{bonus} bonus</p>}
          </div>
          <span className="font-display text-lg font-black text-muted">{job.poster_name}</span>
        </div>

        <SpecGrid
          items={[
            ["Location", job.location],
            ["Pay", pay],
            ["Route", labelize(job.route_type)],
            ["CDL", job.cdl_class ? `Class ${job.cdl_class}` : ""],
            ["Miles/wk", job.avg_miles_week ? job.avg_miles_week.toLocaleString() : ""],
            ["Experience", experienceLabel(job.experience_months)],
          ]}
        />

        <Tags items={[...job.equipment, ...job.endorsements, ...job.freight_types]} />

        {job.description && (
          <section className="grid gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted">Job description</h2>
            <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed">{job.description}</p>
          </section>
        )}

        {!isOwner && (
          <div className="flex flex-wrap gap-2">
            {isDriver && (
              <Button onClick={onApply} disabled={busy || job.status !== "open"}>
                <span className="flex items-center gap-2">
                  <Icon name="verify" className="h-4 w-4" />
                  {job.status === "open" ? "Apply with driver profile" : "Closed"}
                </span>
              </Button>
            )}
            <SaveButton kind="jobs" id={job.id} />
            <Link to={`/messages/to/${job.poster_profile_id}`}>
              <Button variant="ghost">
                <span className="flex items-center gap-2"><Icon name="messages" className="h-4 w-4" /> Message</span>
              </Button>
            </Link>
          </div>
        )}

        {showGate && <GatedNotice>Verify to apply — complete identity + credential verification.</GatedNotice>}
        {note && <p className="font-mono text-sm text-muted">{note}</p>}
      </div>

      <MetaFooter
        items={[
          ["Job ID", shortId("JB", job.id)],
          ["Posted", new Date(job.created_at).toLocaleDateString()],
          ["Employment", labelize(job.employment_type)],
          ["Status", job.status.toUpperCase()],
        ]}
      />

      {job.benefits.length > 0 && (
        <Panel title="Benefits"><Tags items={job.benefits} /></Panel>
      )}

      {/* Poster manages applicants */}
      {isOwner && (
        <Panel
          title={`Applicants (${applications.length})`}
          action={<Button variant="ghost" onClick={toggleStatus}>{job.status === "open" ? "Close job" : "Reopen"}</Button>}
        >
          {applications.length === 0 ? (
            <p className="font-mono text-sm text-muted">No applications yet.</p>
          ) : (
            <ul className="grid gap-3">
              {applications.map((a) => (
                <li key={a.id} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/p/${a.applicant_profile_id}`} className="font-bold hover:underline">{a.display_name ?? "—"}</Link>
                    <span className="font-mono text-xs uppercase text-muted">{a.status}</span>
                  </div>
                  {a.cover_note && <p className="mt-1 text-sm text-muted">{a.cover_note}</p>}
                  <div className="mt-2 flex gap-2">
                    {REVIEW_ACTIONS.map((s) => (
                      <Button key={s} variant="ghost" disabled={a.status === s} onClick={() => decide(a.id, s)}>{s}</Button>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </div>
  );
}
